'use client';
import { useEffect } from 'react';
import { Box, Divider, List, useMediaQuery, useTheme } from '@mui/material';

import {
  ComponentErrorBoundary,
  LiveBadge,
  SidebarSectionTitle,
  SportsFilterButton,
  SportsFuturesIcon,
  SportsMobileSidebar,
  SportsSidebarList,
  SportsSidebarSkeleton,
  withErrorBoundary,
} from '@/components';
import { useSportsSidebar, useT } from '@/hooks';

interface SportsSidebarProps {
  sidebarRef?: React.RefObject<HTMLDivElement | null>;
  onSettingsClick?: () => void;
}

/**
 * SportsSidebar handles the layout and state for the sports navigation menu.
 * It automatically switches to a mobile-optimized view for smaller screens.
 */
export function SportsSidebar({ sidebarRef, onSettingsClick }: SportsSidebarProps) {
  const { t } = useT();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const sportSidebarContext = useSportsSidebar();
  const { state, handlers } = sportSidebarContext;
  const { activeId, sportSlug, isLoading, isError, allItems } = state;
  const { handleCategoryClick, refetch } = handlers;

  // --- Sequential Scroll Proxying ---
  // When the sidebar hits its top or bottom boundary, we proxy the wheel events
  // to the main sports content area to create a seamless "chaining" effect.
  useEffect(() => {
    if (!isDesktop || isLoading) return;

    const sidebarList = document.getElementById('sports-sidebar-scroll-container');
    const mainContent = document.getElementById('sports-main-content');

    if (!sidebarList || !mainContent) return;

    const handleWheel = (e: WheelEvent) => {
      const isAtBottom =
        sidebarList.scrollTop + sidebarList.clientHeight >= sidebarList.scrollHeight - 1;
      const isAtTop = sidebarList.scrollTop <= 0;
      const isScrollingDown = e.deltaY > 0;
      const isScrollingUp = e.deltaY < 0;

      // Check if we should proxy the scroll to the adjacent content
      if ((isAtBottom && isScrollingDown) || (isAtTop && isScrollingUp)) {
        mainContent.scrollBy({
          top: e.deltaY,
          behavior: 'auto',
        });

        // We don't preventDefault here to allow the browser to handle
        // any secondary effects if needed, but we've manually moved the adjacent content.
      }
    };

    sidebarList.addEventListener('wheel', handleWheel, { passive: true });
    return () => sidebarList.removeEventListener('wheel', handleWheel);
  }, [isDesktop, isLoading]);

  if (isLoading) {
    return (
      <>
        <Box sx={{ display: { xs: 'block', lg: 'none' }, width: '100%' }}>
          <SportsSidebarSkeleton isMobile={true} />
        </Box>
        <Box sx={{ width: 240, flexShrink: 0, display: { xs: 'none', lg: 'block' } }}>
          <SportsSidebarSkeleton />
        </Box>
      </>
    );
  }

  // Helper flags for top-level navigation states
  const isLiveActive = sportSlug === 'live';
  const isFuturesActive = sportSlug === 'futures';

  // Navigation handlers for top-level filters
  const handleLiveClick = () =>
    handleCategoryClick({
      sport: 'live',
      category: t('sports.live', { defaultValue: 'Live' }),
    } as any);

  const handleFuturesClick = () =>
    handleCategoryClick({ sport: 'futures', category: t('sports.futures') } as any);

  const liveVariant = isLiveActive ? 'contained' : 'outlined';
  const futuresVariant = isFuturesActive ? 'contained' : 'outlined';

  const futuresStartIcon = <SportsFuturesIcon />;

  return (
    <>
      <Box sx={{ display: { xs: 'block', lg: 'none' }, width: '100%' }}>
        <SportsMobileSidebar
          sidebarContext={sportSidebarContext}
          onSettingsClick={onSettingsClick}
        />
      </Box>

      {/* Desktop View: Render a sticky sidebar with filters and a scrollable list */}
      <Box sx={{ width: 240, flexShrink: 0, display: { xs: 'none', lg: 'block' } }}>
        <Box
          ref={sidebarRef}
          sx={{
            p: 2,
            bgcolor: 'background.paper',
            borderRadius: 2,
            alignSelf: 'flex-start',
            maxHeight: 'calc(100vh - 160px)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Top level navigation buttons: Live & Futures */}
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <SportsFilterButton
              label={
                <LiveBadge
                  dotSize={8}
                  fontSize="0.875rem"
                  sx={{
                    mr: 1,
                    color: 'error.main',
                  }}
                />
              }
              isActive={isLiveActive}
              onClick={handleLiveClick}
              variant={liveVariant}
              sx={{ flex: 1 }}
            />
            <SportsFilterButton
              label={t('sports.futures')}
              isActive={isFuturesActive}
              onClick={handleFuturesClick}
              startIcon={futuresStartIcon}
              variant={futuresVariant}
              sx={{ flex: 1 }}
            />
          </Box>

          <Divider sx={{ my: 2, flexShrink: 0 }} />

          <SidebarSectionTitle sx={{ flexShrink: 0 }}>{t('sports.allSports')}</SidebarSectionTitle>

          {/* Scrollable list of sports categories */}
          <List
            id="sports-sidebar-scroll-container"
            sx={{
              p: 0,
              overflowY: 'auto',
              flex: 1,
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: 'divider',
                borderRadius: '3px',
                '&:hover': { bgcolor: 'text.secondary' },
              },
            }}
          >
            <ComponentErrorBoundary componentName="SportsSidebarList">
              <SportsSidebarList
                isLoading={isLoading}
                isError={isError}
                items={allItems as any}
                activeId={activeId}
                onCategoryClick={handleCategoryClick}
                onRetry={refetch}
              />
            </ComponentErrorBoundary>
          </List>
        </Box>
      </Box>
    </>
  );
}

export default withErrorBoundary(SportsSidebar, { componentName: 'SportsSidebar' });
